// ============================================
// Utils - Error Handling (ES6)
// 표준화된 에러 처리 유틸리티
// ============================================

import { showToast as displayToast } from './toast.js';
import { CURRENCY } from '../constants.js';

// ============================================
// 에러 타입 정의
// ============================================

export const ErrorTypes = {
  NETWORK: 'NETWORK',
  API: 'API',
  AUTH: 'AUTH',
  VALIDATION: 'VALIDATION',
  UNKNOWN: 'UNKNOWN'
};

// ============================================
// 에러 메시지 매핑
// ============================================

const ERROR_MESSAGES = {
  [ErrorTypes.NETWORK]: '네트워크 연결을 확인해주세요.',
  [ErrorTypes.API]: '서버 오류가 발생했습니다.',
  [ErrorTypes.AUTH]: '인증이 필요합니다.',
  [ErrorTypes.VALIDATION]: '입력값을 확인해주세요.',
  [ErrorTypes.UNKNOWN]: '오류가 발생했습니다.'
};

// HTTP 상태 코드별 메시지
const HTTP_STATUS_MESSAGES = {
  400: '잘못된 요청입니다.',
  401: '로그인이 필요합니다.',
  402: `${CURRENCY.NAME}가 부족합니다.`,
  403: '권한이 없습니다.',
  404: '요청한 리소스를 찾을 수 없습니다.',
  429: '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.',
  500: '서버 오류가 발생했습니다.',
  502: '서버에 연결할 수 없습니다.',
  503: '서버가 일시적으로 이용 불가합니다.'
};

// ============================================
// 에러 분류 함수
// ============================================

/**
 * 에러를 타입별로 분류
 * @param {Error|Response} error - 에러 객체 또는 Response
 * @returns {string} ErrorTypes 중 하나
 */
export function classifyError(error) {
  if (!navigator.onLine) return ErrorTypes.NETWORK;
  if (error instanceof TypeError && error.message.includes('fetch')) return ErrorTypes.NETWORK;
  if (error?.status === 401 || error?.status === 403) return ErrorTypes.AUTH;
  if (error?.status >= 400 && error?.status < 500) return ErrorTypes.VALIDATION;
  if (error?.status >= 500) return ErrorTypes.API;
  return ErrorTypes.UNKNOWN;
}

// ============================================
// 에러 핸들러
// ============================================

/**
 * API 응답 에러 처리
 * @param {Response} response - fetch Response 객체
 * @returns {Promise<Object>} 에러 정보 객체
 */
export async function handleApiError(response) {
  let errorData = {};

  try {
    errorData = await response.json();
  } catch {
    // JSON 파싱 실패시 빈 객체 유지
  }

  const errorType = classifyError(response);
  const defaultMessage = HTTP_STATUS_MESSAGES[response.status] || ERROR_MESSAGES[errorType];

  return {
    type: errorType,
    status: response.status,
    message: errorData.error || errorData.message || defaultMessage,
    data: errorData
  };
}

/**
 * 표준 에러 처리 - Toast와 로깅 통합
 * @param {Error} error - 에러 객체
 * @param {string} context - 에러 발생 컨텍스트 (함수명 등)
 * @param {Object} options - 추가 옵션
 */
export function handleError(error, context = '', options = {}) {
  const {
    showToast = true,
    logToConsole = true,
    customMessage = null
  } = options;

  const errorType = classifyError(error);
  const message = customMessage || error.message || ERROR_MESSAGES[errorType];

  if (logToConsole) {
    console.error(`[${context}] ${errorType}:`, error);
  }

  if (showToast) {
    displayToast(message, 'error');
  }

  return { type: errorType, message };
}

// ============================================
// 안전한 비동기 실행
// ============================================

/**
 * 안전한 비동기 함수 실행 (에러 핸들링 포함)
 * @param {Function} asyncFn - 실행할 비동기 함수
 * @param {Object} options - 옵션
 * @returns {Promise<[Error|null, any]>} [에러, 결과] 튜플
 */
export async function safeAsync(asyncFn, options = {}) {
  const { context = 'Unknown', onError = null } = options;

  try {
    const result = await asyncFn();
    return [null, result];
  } catch (error) {
    handleError(error, context, options);
    if (onError) onError(error);
    return [error, null];
  }
}

/**
 * 안전한 JSON 파싱
 * @param {string} jsonString - JSON 문자열
 * @param {any} fallback - 파싱 실패시 반환값
 * @returns {any} 파싱된 객체 또는 fallback
 */
export function safeJsonParse(jsonString, fallback = null) {
  try {
    return JSON.parse(jsonString);
  } catch {
    return fallback;
  }
}

// ============================================
// API 호출 래퍼
// ============================================

/**
 * 에러 처리가 포함된 fetch 래퍼
 * @param {string} url - 요청 URL
 * @param {Object} options - fetch 옵션
 * @param {string} context - 에러 컨텍스트
 * @returns {Promise<Object>} 응답 데이터
 */
export async function safeFetch(url, options = {}, context = 'API') {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorInfo = await handleApiError(response);
      throw new ApiError(errorInfo.message, errorInfo);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) throw error;

    handleError(error, context, { showToast: true });
    throw error;
  }
}

// ============================================
// 커스텀 에러 클래스
// ============================================

export class ApiError extends Error {
  constructor(message, info = {}) {
    super(message);
    this.name = 'ApiError';
    this.type = info.type || ErrorTypes.API;
    this.status = info.status;
    this.data = info.data;
  }
}
